import React, { useState } from 'react';
import { simularPrestamo } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Calculator, TrendingDown, PieChart as PieChartIcon, BarChart as BarChartIcon } from 'lucide-react';
import { 
    AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
    PieChart, Pie, Cell, BarChart, Bar, Legend, ComposedChart, Line
} from 'recharts';

const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-DO', {
        style: 'currency',
        currency: 'DOP',
        minimumFractionDigits: 2,
    }).format(value);
};

const COLORS = ['hsl(161, 91%, 16%)', 'hsl(160, 84%, 39%)', 'hsl(28, 87%, 37%)'];

export default function SimuladorPage() {
    const [loading, setLoading] = useState(false);
    const [resultado, setResultado] = useState(null);
    const [form, setForm] = useState({
        monto: '50000',
        tasa_interes: '10',
        plazo: '12',
        frecuencia: 'mensual',
        tipo_calculo: 'simple',
    });

    const handleSimular = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const data = {
                monto: parseFloat(form.monto),
                tasa_interes: parseFloat(form.tasa_interes),
                plazo: parseInt(form.plazo),
                frecuencia: form.frecuencia,
                tipo_calculo: form.tipo_calculo,
            };
            const { data: result } = await simularPrestamo(data);
            setResultado(result);
        } catch (error) {
            toast.error('Error al simular préstamo');
        } finally {
            setLoading(false);
        }
    };

    const frecuenciaLabel = {
        diario: 'días',
        semanal: 'semanas',
        quincenal: 'quincenas',
        mensual: 'meses',
    };

    return (
        <div className="space-y-6" data-testid="simulador-page">
            {/* Header */}
            <div>
                <h1 className="font-heading text-2xl sm:text-3xl font-bold">Simulador de Préstamo</h1>
                <p className="text-muted-foreground mt-1">Calcula cuotas y visualiza la amortización</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Form */}
                <Card className="lg:col-span-1">
                    <CardHeader>
                        <CardTitle className="font-heading text-lg flex items-center gap-2">
                            <Calculator className="w-5 h-5" />
                            Parámetros
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <form onSubmit={handleSimular} className="space-y-4">
                            <div className="space-y-2">
                                <Label>Monto del Préstamo</Label>
                                <Input
                                    type="number"
                                    value={form.monto}
                                    onChange={(e) => setForm({ ...form, monto: e.target.value })}
                                    required
                                    min="100"
                                    data-testid="sim-monto"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Tasa de Interés (%)</Label>
                                <Input
                                    type="number"
                                    value={form.tasa_interes}
                                    onChange={(e) => setForm({ ...form, tasa_interes: e.target.value })}
                                    required
                                    min="0"
                                    step="0.1"
                                    data-testid="sim-tasa"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Plazo (períodos)</Label>
                                <Input
                                    type="number"
                                    value={form.plazo}
                                    onChange={(e) => setForm({ ...form, plazo: e.target.value })}
                                    required
                                    min="1"
                                    data-testid="sim-plazo"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Frecuencia de Pago</Label>
                                <Select
                                    value={form.frecuencia}
                                    onValueChange={(value) => setForm({ ...form, frecuencia: value })}
                                >
                                    <SelectTrigger data-testid="sim-frecuencia">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="diario">Diario</SelectItem>
                                        <SelectItem value="semanal">Semanal</SelectItem>
                                        <SelectItem value="quincenal">Quincenal</SelectItem>
                                        <SelectItem value="mensual">Mensual</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-2">
                                <Label>Tipo de Cálculo</Label>
                                <Select
                                    value={form.tipo_calculo}
                                    onValueChange={(value) => setForm({ ...form, tipo_calculo: value })}
                                >
                                    <SelectTrigger data-testid="sim-tipo">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="simple">Interés Simple</SelectItem>
                                        <SelectItem value="frances">Sistema Francés</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <Button type="submit" className="w-full" disabled={loading} data-testid="sim-calcular-btn">
                                {loading ? 'Calculando...' : 'Calcular'}
                            </Button>
                        </form>
                    </CardContent>
                </Card>

                {/* Results */}
                <div className="lg:col-span-2 space-y-6">
                    {resultado ? (
                        <>
                            {/* Summary Cards */}
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                <Card className="animate-fadeIn">
                                    <CardContent className="p-4 text-center">
                                        <p className="text-sm text-muted-foreground">Monto</p>
                                        <p className="text-xl font-heading font-bold">{formatCurrency(resultado.monto)}</p>
                                    </CardContent>
                                </Card>
                                <Card className="animate-fadeIn stagger-1">
                                    <CardContent className="p-4 text-center">
                                        <p className="text-sm text-muted-foreground">Cuota</p>
                                        <p className="text-xl font-heading font-bold text-primary">{formatCurrency(resultado.cuota_fija)}</p>
                                    </CardContent>
                                </Card>
                                <Card className="animate-fadeIn stagger-2">
                                    <CardContent className="p-4 text-center">
                                        <p className="text-sm text-muted-foreground">Total Intereses</p>
                                        <p className="text-xl font-heading font-bold text-warning">{formatCurrency(resultado.total_interes)}</p>
                                    </CardContent>
                                </Card>
                                <Card className="animate-fadeIn stagger-3">
                                    <CardContent className="p-4 text-center">
                                        <p className="text-sm text-muted-foreground">Total a Pagar</p>
                                        <p className="text-xl font-heading font-bold text-accent">{formatCurrency(resultado.total_pagar)}</p>
                                    </CardContent>
                                </Card>
                            </div>

                            {/* Charts */}
                            <Tabs defaultValue="evolucion" className="space-y-4">
                                <TabsList>
                                    <TabsTrigger value="evolucion">
                                        <TrendingDown className="w-4 h-4 mr-2" />
                                        Evolución
                                    </TabsTrigger>
                                    <TabsTrigger value="composicion">
                                        <BarChartIcon className="w-4 h-4 mr-2" />
                                        Composición
                                    </TabsTrigger>
                                    <TabsTrigger value="distribucion">
                                        <PieChartIcon className="w-4 h-4 mr-2" />
                                        Distribución
                                    </TabsTrigger>
                                </TabsList>

                                <TabsContent value="evolucion">
                                    <Card>
                                        <CardHeader>
                                            <CardTitle className="font-heading text-lg">Evolución del Saldo</CardTitle>
                                            <CardDescription>Saldo pendiente vs pagado por cuota</CardDescription>
                                        </CardHeader>
                                        <CardContent>
                                            <div className="h-80">
                                                <ResponsiveContainer width="100%" height="100%">
                                                    <AreaChart data={resultado.graficos.evolucion_saldo}>
                                                        <defs>
                                                            <linearGradient id="colorSaldo" x1="0" y1="0" x2="0" y2="1">
                                                                <stop offset="5%" stopColor="hsl(161, 91%, 16%)" stopOpacity={0.3}/>
                                                                <stop offset="95%" stopColor="hsl(161, 91%, 16%)" stopOpacity={0}/>
                                                            </linearGradient>
                                                            <linearGradient id="colorPagado" x1="0" y1="0" x2="0" y2="1">
                                                                <stop offset="5%" stopColor="hsl(160, 84%, 39%)" stopOpacity={0.3}/>
                                                                <stop offset="95%" stopColor="hsl(160, 84%, 39%)" stopOpacity={0}/>
                                                            </linearGradient>
                                                        </defs>
                                                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(240, 6%, 85%)" />
                                                        <XAxis dataKey="cuota" stroke="hsl(240, 4%, 46%)" fontSize={12} />
                                                        <YAxis tickFormatter={(val) => `$${(val/1000).toFixed(0)}k`} stroke="hsl(240, 4%, 46%)" fontSize={12} />
                                                        <Tooltip formatter={(value) => formatCurrency(value)} />
                                                        <Legend />
                                                        <Area type="monotone" dataKey="saldo" name="Saldo Pendiente" stroke="hsl(161, 91%, 16%)" fill="url(#colorSaldo)" strokeWidth={2} />
                                                        <Area type="monotone" dataKey="pagado" name="Total Pagado" stroke="hsl(160, 84%, 39%)" fill="url(#colorPagado)" strokeWidth={2} />
                                                    </AreaChart>
                                                </ResponsiveContainer>
                                            </div>
                                        </CardContent>
                                    </Card>
                                </TabsContent>

                                <TabsContent value="composicion">
                                    <Card>
                                        <CardHeader>
                                            <CardTitle className="font-heading text-lg">Composición de Cuotas</CardTitle>
                                            <CardDescription>Capital vs Interés por cuota</CardDescription>
                                        </CardHeader>
                                        <CardContent>
                                            <div className="h-80">
                                                <ResponsiveContainer width="100%" height="100%">
                                                    <ComposedChart data={resultado.graficos.composicion_cuotas.slice(0, 24)}>
                                                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(240, 6%, 85%)" />
                                                        <XAxis dataKey="cuota" stroke="hsl(240, 4%, 46%)" fontSize={12} />
                                                        <YAxis tickFormatter={(val) => `$${(val/1000).toFixed(1)}k`} stroke="hsl(240, 4%, 46%)" fontSize={12} />
                                                        <Tooltip formatter={(value) => formatCurrency(value)} />
                                                        <Legend />
                                                        <Bar dataKey="capital" name="Capital" fill="hsl(161, 91%, 16%)" stackId="a" />
                                                        <Bar dataKey="interes" name="Interés" fill="hsl(28, 87%, 37%)" stackId="a" />
                                                    </ComposedChart>
                                                </ResponsiveContainer>
                                            </div>
                                        </CardContent>
                                    </Card>
                                </TabsContent>

                                <TabsContent value="distribucion">
                                    <Card>
                                        <CardHeader>
                                            <CardTitle className="font-heading text-lg">Distribución del Pago Total</CardTitle>
                                            <CardDescription>Capital vs Intereses totales</CardDescription>
                                        </CardHeader>
                                        <CardContent>
                                            <div className="h-80 flex items-center justify-center">
                                                <ResponsiveContainer width="100%" height="100%">
                                                    <PieChart>
                                                        <Pie
                                                            data={resultado.graficos.distribucion}
                                                            cx="50%"
                                                            cy="50%"
                                                            innerRadius={80}
                                                            outerRadius={120}
                                                            paddingAngle={4}
                                                            dataKey="value"
                                                            label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                                                        >
                                                            {resultado.graficos.distribucion.map((entry, index) => (
                                                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                                            ))}
                                                        </Pie>
                                                        <Tooltip formatter={(value) => formatCurrency(value)} />
                                                    </PieChart>
                                                </ResponsiveContainer>
                                            </div>
                                        </CardContent>
                                    </Card>
                                </TabsContent>
                            </Tabs>

                            {/* Amortization Table */}
                            <Card>
                                <CardHeader>
                                    <CardTitle className="font-heading text-lg">Tabla de Amortización</CardTitle>
                                    <CardDescription>
                                        {resultado.plazo} {frecuenciaLabel[resultado.frecuencia]} • {resultado.tipo_calculo === 'simple' ? 'Interés Simple' : 'Sistema Francés'}
                                    </CardDescription>
                                </CardHeader>
                                <CardContent className="p-0">
                                    <div className="overflow-x-auto max-h-96">
                                        <Table>
                                            <TableHeader className="sticky top-0 bg-background">
                                                <TableRow>
                                                    <TableHead className="text-center">#</TableHead>
                                                    <TableHead>Fecha</TableHead>
                                                    <TableHead className="text-right">Capital</TableHead>
                                                    <TableHead className="text-right">Interés</TableHead>
                                                    <TableHead className="text-right">Cuota</TableHead>
                                                    <TableHead className="text-right">Saldo</TableHead>
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                {resultado.cronograma.map((cuota) => (
                                                    <TableRow key={cuota.numero}>
                                                        <TableCell className="text-center font-mono">{cuota.numero}</TableCell>
                                                        <TableCell>{cuota.fecha_vencimiento}</TableCell>
                                                        <TableCell className="text-right font-mono">{formatCurrency(cuota.capital)}</TableCell>
                                                        <TableCell className="text-right font-mono text-warning">{formatCurrency(cuota.interes)}</TableCell>
                                                        <TableCell className="text-right font-mono font-bold">{formatCurrency(cuota.total)}</TableCell>
                                                        <TableCell className="text-right font-mono text-muted-foreground">{formatCurrency(cuota.saldo_capital)}</TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </div>
                                </CardContent>
                            </Card>
                        </>
                    ) : (
                        <Card className="h-96 flex items-center justify-center">
                            <div className="text-center">
                                <Calculator className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
                                <p className="text-muted-foreground">Ingresa los parámetros y presiona Calcular</p>
                                <p className="text-sm text-muted-foreground mt-1">para ver la simulación del préstamo</p>
                            </div>
                        </Card>
                    )}
                </div>
            </div>
        </div>
    );
}
